import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { switchMap } from 'rxjs/operators';

// ==========================================
// CUSTOM VALIDATORS
// ==========================================

class CustomValidators {
    static usuario(control: any) {
        const value = control.value?.trim() || '';

        if (!value) {
            return { required: 'El usuario es requerido' };
        }

        if (value.length < 3) {
            return { minlength: 'El usuario debe tener al menos 3 caracteres' };
        }

        if (value.length > 50) {
            return { maxlength: 'El usuario no puede exceder 50 caracteres' };
        }

        const pattern = /^[A-Za-z0-9_]+$/;
        if (!pattern.test(value)) {
            return { pattern: 'Solo letras, números y guión bajo permitidos' };
        }

        return null;
    }

    static contrasena(control: any) {
        const value = control.value || '';

        if (!value) {
            return { required: 'La contraseña es requerida' };
        }

        if (value.length < 8) {
            return { minlength: 'La contraseña debe tener al menos 8 caracteres' };
        }

        if (value.length > 64) {
            return { maxlength: 'La contraseña no puede exceder 64 caracteres' };
        }

        return null;
    }
}

// ==========================================
// LOGIN COMPONENT
// ==========================================

@Component({
    selector: 'app-login',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent {
    private readonly fb = inject(FormBuilder);
    private readonly authService = inject(AuthService);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);

    // Form
    loginForm!: FormGroup;

    // State
    isLoading = signal(false);
    showPassword = signal(false);
    errorMessage = signal('');
    successMessage = signal('');
    returnUrl: string = '/admin/dashboard';

    // Field touched state
    usuarioTouched = signal(false);
    contrasenaTouched = signal(false);

    constructor() {
        // Get return URL from route parameters or default to dashboard
        this.returnUrl = this.activatedRoute.snapshot.queryParams['returnUrl'] || '/admin/dashboard';
        this.initializeForm();
        this.checkExistingAuth();
    }

    /**
     * Initialize login form
     */
    private initializeForm(): void {
        this.loginForm = this.fb.group({
            usuario: ['', [CustomValidators.usuario]],
            contrasena: ['', [CustomValidators.contrasena]],
            rememberMe: [false]
        });
    }

    /**
     * Check if user is already authenticated
     */
    private checkExistingAuth(): void {
        if (this.authService.isAuthenticated()) {
            // Optionally redirect to dashboard
            // this.router.navigate(['/admin/dashboard']);
        }
    }

    /**
     * Get error message for a field
     */
    getFieldError(field: 'usuario' | 'contrasena'): string {
        const control = this.loginForm.get(field);
        if (!control || !control.errors) {
            return '';
        }

        const errors = control.errors;
        const errorKey = Object.keys(errors)[0];
        return errors[errorKey];
    }

    /**
     * Check if field should show error
     */
    shouldShowError(field: 'usuario' | 'contrasena'): boolean {
        const control = this.loginForm.get(field);
        const touched = field === 'usuario' ? this.usuarioTouched() : this.contrasenaTouched();
        return !!control && control.invalid && touched;
    }

    /**
     * Check if field is valid and touched
     */
    isFieldSuccess(field: 'usuario' | 'contrasena'): boolean {
        const control = this.loginForm.get(field);
        const touched = field === 'usuario' ? this.usuarioTouched() : this.contrasenaTouched();
        return !!control && control.valid && touched;
    }

    /**
     * Mark field as touched
     */
    markAsTouched(field: 'usuario' | 'contrasena'): void {
        if (field === 'usuario') {
            this.usuarioTouched.set(true);
        } else {
            this.contrasenaTouched.set(true);
        }
    }

    /**
     * Clear field error on focus
     */
    clearFieldError(field: 'usuario' | 'contrasena'): void {
        const touched = field === 'usuario' ? this.usuarioTouched() : this.contrasenaTouched();
        if (touched) {
            const control = this.loginForm.get(field);
            control?.updateValueAndValidity();
        }
    }

    /**
     * Toggle password visibility
     */
    togglePassword(): void {
        this.showPassword.update(show => !show);
    }

    /**
     * Clear messages
     */
    private clearMessages(): void {
        this.errorMessage.set('');
        this.successMessage.set('');
    }

    /**
     * Handle form submission
     */
    onSubmit(): void {
        // Clear previous messages
        this.clearMessages();

        // Mark all fields as touched
        this.usuarioTouched.set(true);
        this.contrasenaTouched.set(true);

        // Validate form
        if (this.loginForm.invalid) {
            return;
        }

        // Get form values
        const { usuario, contrasena, rememberMe } = this.loginForm.value;

        // Set loading state
        this.isLoading.set(true);

        // Attempt login
        this.authService.login(usuario.trim(), contrasena, rememberMe).pipe(
            // After successful login, fetch user permissions
            switchMap(() => this.authService.getUserPermissions())
        ).subscribe({
            next: () => {
                this.isLoading.set(false);
                this.successMessage.set('¡Inicio de sesión exitoso! Redirigiendo...');

                // Redirect to return URL or dashboard after 1.5 seconds
                setTimeout(() => {
                    this.router.navigateByUrl(this.returnUrl);
                }, 1500);
            },
            error: (error) => {
                this.isLoading.set(false);
                this.errorMessage.set(error.message || 'Error al iniciar sesión');

                // Handle field-specific errors from API
                if (error.details && error.details.length > 0) {
                    error.details.forEach((detail: any) => {
                        const control = this.loginForm.get(detail.field);
                        if (control) {
                            control.setErrors({ api: detail.issue });
                        }
                    });
                }
            }
        });
    }
}
